import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import * as Yup from "yup";
import React, { FC, useState } from "react";
import { Form } from "assets/styleds";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApplyProps } from "hooks/useApplyProps";
import { useAppSelector } from "store";
import {
  ENotificationChannel,
  INotification,
  IPrepareNotification,
} from "@deltasge/marauders-map";
import { format } from "date-fns";
import { api, apiPush } from "configs";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";

interface DialogSendNotificationProps {
  hide: () => void;
  token?: string;
}

const yupObject = Yup.object({
  to: Yup.array().of(Yup.string().required()).required().label("Para"),
  title: Yup.string().required().label("Título"),
  body: Yup.string().required().label("Corpo"),
  enviarParaMimSemFila: Yup.boolean().default(false),
});

type IForm = Yup.InferType<typeof yupObject>;

const yupSchema = yupObject.shape({});

export const DialogSendNotification: FC<DialogSendNotificationProps> = ({
  hide,
  token,
}) => {
  const [enviando, setEnviando] = useState(false);
  const {
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
  } = useForm({
    resolver: yupResolver(yupSchema),
    defaultValues: {
      title: "Titulo da notificação",
      body: "Corpo da notificação",
      to: [],
      enviarParaMimSemFila: false,
    },
  });

  const applayProps = useApplyProps({ register, errors, schema: yupSchema });

  const to = watch("to") as string[];
  const enviarParaMimSemFila = watch("enviarParaMimSemFila");

  const { deltaId } = usuario || {};

  const onSubmit = async (data: IForm) => {
    try {
      if (!usuario) {
        throw new Error("Não foi possível encontrar o usuário autenticado");
      }
      setEnviando(true);

      if (data.enviarParaMimSemFila) {
        const body: INotification = {
          pushTokens: [token],
          title: data.title,
          body: data.body,
          data: {
            channel: ENotificationChannel.conversas,
            data: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            dataNotificacao: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
            descricao: data.body,
            escolaId: usuario.escolaId,
            modulo: "mensagens",
            remetente: usuario.displayName || usuario.nome,
            titulo: data.title,
            toId: usuario.deltaId,
            fromId: usuario.deltaId,
          },
        };
        await apiPush.post("/api/notifications", body);
      } else {
        if (!data.to || !data.to.length) {
          throw new Error("Nenhum destinatário informado.");
        }
        const notifications: IPrepareNotification[] = data.to.map((dest) => {
          return {
            body: data.body,
            title: data.title,
            toIds: [dest],
            to: [],
            fromId: usuario.deltaId,
            data: {
              channel: ENotificationChannel.conversas,
              data: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
              dataNotificacao: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
              descricao: data.body,
              escolaId: usuario.escolaId,
              modulo: "mensagens",
              remetente: usuario.displayName || usuario.nome,
              titulo: data.title,
              toId: dest,
              fromId: usuario.deltaId,
            },
          };
        });
        await api.post("notificacao", notifications);
      }

      snack.success("Notificações enviada com sucesso!");
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setEnviando(false);
    }
  };

  if (!deltaId) return null;

  return (
    <>
      <Box sx={{ height: "100%", overflow: "auto", width: "100%" }}>
        <DialogContent dividers style={{ paddingBottom: 0 }}>
          <Form onSubmit={handleSubmit(onSubmit)} id="send-notification">
            <Grid container spacing={2}>
              {token && (
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enviarParaMimSemFila}
                          onChange={() => {
                            setValue(
                              "enviarParaMimSemFila",
                              !enviarParaMimSemFila
                            );
                          }}
                        />
                      }
                      label="Enviar somente para mim sem usar a fila"
                    />
                  </FormGroup>
                </Grid>
              )}
              {!enviarParaMimSemFila && (
                <Grid item xs={12}>
                  <Autocomplete
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo
                    multiple
                    value={to || []}
                    options={[] as string[]}
                    onChange={(_, value) => {
                      const uniqueValues = Array.from(
                        new Set(
                          value
                            .map((item) =>
                              typeof item === "string" ? item.trim() : item
                            )
                            .filter(Boolean)
                        )
                      );
                      setValue("to", uniqueValues as never[], {
                        shouldValidate: true,
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Para"
                        placeholder={
                          to?.length
                            ? ""
                            : "Informe o DeltaId e pressione Enter"
                        }
                        error={!!errors.to}
                        helperText={errors.to?.message as string | undefined}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment
                                position="start"
                                sx={{ mt: "0 !important" }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={to.includes(deltaId)}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          if (!to.includes(deltaId)) {
                                            setValue(
                                              "to",
                                              [...to, deltaId] as never[],
                                              {
                                                shouldValidate: true,
                                              }
                                            );
                                          }
                                        } else {
                                          setValue(
                                            "to",
                                            to.filter(
                                              (id) => id !== deltaId
                                            ) as never[],
                                            { shouldValidate: true }
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label="Mim mesmo"
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField {...applayProps("title", yupSchema)} />
              </Grid>
              <Grid item xs={12}>
                <TextField {...applayProps("body", yupSchema)} />
              </Grid>
            </Grid>
          </Form>
        </DialogContent>
      </Box>
      <DialogActions>
        <Button size="small" variant="text" onClick={hide} color={"secondary"}>
          Cancelar
        </Button>
        <LoadingButton
          size="small"
          variant="text"
          type="submit"
          form="send-notification"
          loading={enviando}
          disabled={enviando}
        >
          Enviar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
